"use client";

import { Group, Button, Burger, Image, Badge, Box } from "@forestsync/ui";
import { spotlight } from "@mantine/spotlight";
import { useMediaQuery, useMounted } from "@forestsync/ui";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { platformNavItems } from "@/lib/navigation";
import Preferences from "@/components/Preferences";
import { FeedbackButton } from "@/components/layout/FeedbackButton";
import { UserSection } from "./UserSection";

interface HeaderProps {
  opened: boolean;
  toggle: () => void;
  close: () => void;
}

export function Header({ opened, toggle, close }: HeaderProps) {
  const t = useTranslations("navigation");
  const commonT = useTranslations("common");
  const mounted = useMounted();

  const mainNavItems = platformNavItems

  const isMobile = useMediaQuery("(max-width: 48em)", false, {
    getInitialValueInEffect: false,
  });

  const isTablet = useMediaQuery("(max-width: 64em)", false, {
    getInitialValueInEffect: false,
  });

  // Use fallback values on server to prevent hydration mismatch
  const safeIsMobile = mounted ? isMobile : false;
  const safeIsTablet = mounted ? isTablet : false;

  return (
    <Group h="100%" px="md" justify="space-between">
      <Group gap={0}>
        {/* Logo */}
        <Link href="/" passHref>
          <Image
            src="/logo.svg"
            alt="ForestSync"
            h={60}
            w="auto"
            fallbackSrc="/logo.png"
          />
        </Link>
        <Badge
          size="xs"
          variant="gradient"
          gradient={{ from: "forest", to: "teal" }}
          c="var(--mantine-color-body)"
          ml={6}
          mb={-20}
        >
          BETA
        </Badge>

        {/* Desktop Navigation */}
        {!safeIsMobile && (
          <Group ml="xl" gap="xs">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const size = safeIsTablet ? "xs" : "sm";
              return (
                <Box key={item.href} className="relative">
                  {item.spotlightTrigger ? (
                    <Button
                      variant="subtle"
                      color={item.color || "gray"}
                      leftSection={<Icon size={16} />}
                      size={size}
                      onClick={() => { close(); spotlight.open(); }}
                    >
                      {t(item.id)}
                    </Button>
                  ) : (
                    <Button
                      component={Link}
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      variant="subtle"
                      color={item.color || "gray"}
                      leftSection={<Icon size={16} />}
                      disabled={item.disabled}
                      size={size}
                      onClick={close}
                      className={item.disabled ? "opacity-75" : ""}
                    >
                      {t(item.id)}
                    </Button>
                  )}
                  {item.disabled && (
                    <Badge
                      size="xs"
                      variant="gradient"
                      gradient={{ from: "timber", to: "forest" }}
                      c="var(--mantine-color-body)"
                      className="absolute -top-1 -right-1 z-10"
                    >
                      {commonT("soon")}
                    </Badge>
                  )}
                </Box>
              );
            })}
          </Group>
        )}
      </Group>

      <Group>
        {/* Desktop Preferences and User Section */}
        {!safeIsMobile && (
          <>
            <FeedbackButton />
            <Preferences size={safeIsTablet ? "md" : "lg"} />
            <UserSection size={safeIsTablet ? "xs" : "sm"} />
          </>
        )}

        {/* Mobile Burger */}
        {safeIsMobile && <Burger opened={opened} onClick={toggle} size="sm" />}
      </Group>
    </Group>
  );
}
